import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Subscription, timer, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { AppState } from './app-state';
import { MaintenanceState } from '../reducers/maintenance.reducer';
import { MaintenanceActions } from '../actions/maintenance.actions';

declare var Penmate;

@Injectable({ providedIn: 'root' })
export class MaintenanceService implements OnDestroy {
  private pollingSubscription: Subscription;
  private maintenanceStateSubscription: Subscription;
  private currentState: MaintenanceState;
  private readonly pollIntervalMs = 60000;
  private maintenanceModalOverride = false;

  maintenance$ = this.store.select(state => state.maintenance);

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private maintenanceActions: MaintenanceActions,
  ) {
    this.maintenanceStateSubscription = this.maintenance$.subscribe(state => {
      this.currentState = state;
    });
  }

  initialize() {
    try {
      const initialState = (typeof Penmate !== 'undefined' && Penmate.maintenanceState)
        ? Penmate.maintenanceState
        : null;
      if (initialState && initialState.active) {
        this.store.dispatch(this.maintenanceActions.setMaintenanceState(initialState));
      }
    } catch (err) {
      console.warn('[maintenance] failed to bootstrap state', err);
    }
    if (!this.pollingSubscription) {
      this.checkStatus().subscribe();
      this.pollingSubscription = timer(this.pollIntervalMs, this.pollIntervalMs)
        .pipe(switchMap(() => this.checkStatus(true)))
        .subscribe();
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    if (this.maintenanceStateSubscription) {
      this.maintenanceStateSubscription.unsubscribe();
    }
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  checkStatus(isBackground: boolean = false) {
    const apiBase =
      typeof Penmate !== 'undefined' && Penmate.env && Penmate.env.apiUrl
        ? Penmate.env.apiUrl
        : '/api';
    const url = `${apiBase}/status`;

    return this.http.get(url).pipe(
      tap((payload: any) => {
        this.store.dispatch(this.maintenanceActions.setMaintenanceState(payload));
      }),
      catchError((error: HttpErrorResponse) => {
        this.store.dispatch(
          this.maintenanceActions.maintenanceCheckFailed({
            error,
            isBackground,
          }),
        );
        return of(error);
      }),
    );
  }

  refreshStatus() {
    return this.checkStatus(false);
  }

  isActive(): boolean {
    return !!(this.currentState && this.currentState.active);
  }

  getCurrentState(): MaintenanceState {
    return this.currentState;
  }

  setMaintenanceModalOverride(value: boolean) {
    this.maintenanceModalOverride = value;
    this.emitCurrentState();
  }

  shouldForceMaintenanceModal(): boolean {
    return this.maintenanceModalOverride;
  }

  private emitCurrentState() {
    if (!this.currentState) {
      return;
    }
    const payload = {
      active: this.currentState.active,
      message: this.currentState.message,
      support: this.currentState.support,
      retryAfter: this.currentState.retryAfter,
      source: this.currentState.source,
      updatedAt: this.currentState.updatedAt,
      raw: this.currentState.raw,
    };
    this.store.dispatch(this.maintenanceActions.setMaintenanceState(payload));
  }

  handleMaintenancePayload(payload: any) {
    if (payload) {
      if (payload.source === 'default') {
        return;
      }
      if (
        this.currentState &&
        this.currentState.active &&
        (!payload.active || payload.active === false) &&
        payload.source === 'default'
      ) {
        return;
      }
      this.store.dispatch(this.maintenanceActions.setMaintenanceState(payload));
    }
  }

  handleMaintenanceError(error: any) {
    if (!error) {
      return;
    }

    const status = error && (error.status || error.statusCode);
    if (status === 503) {
      const payload =
        (error && error.error && (error.error.maintenance || error.error)) ||
        (error && error.payload);
      if (payload) {
        this.handleMaintenancePayload(payload);
      }
    }
  }
}
